import ShowElements from "@/components/drawer/ShowElements";
import { TypeDefaultImage } from "@/lib/image";
import { Attachment } from "@/lib/types";
import { ACTION_VIEW_ICON } from "@/components/table/action-icons";
import { Download, PlusCircle, Trash2 } from "lucide-react";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { ACTION_I18N_KEYS } from "./action-i18n-keys";
import {
  TableRowActionsMenu,
  TableRowAction,
} from "./TableRowActionsMenu";

type PreviewElement = {
  label: string;
  value: string | Attachment[];
  type: "text" | "richtext" | "image";
};

type RelatedRecordRowActionsProps = {
  isAddMode?: boolean;
  resourceId: string;
  previewType: TypeDefaultImage;
  previewElements: PreviewElement[];
  onDelete?: () => void;
  onAdd?: () => void;
  printAction?: ReactNode;
  extraActions?: TableRowAction[];
};

export function RelatedRecordRowActions({
  isAddMode = false,
  resourceId,
  previewType,
  previewElements,
  onDelete,
  onAdd,
  printAction,
  extraActions = [],
}: RelatedRecordRowActionsProps) {
  const { t } = useTranslation();
  const [previewOpen, setPreviewOpen] = useState(false);

  const actions: TableRowAction[] = [
    ...extraActions,
    {
      id: "preview",
      label: t(ACTION_I18N_KEYS.view),
      icon: ACTION_VIEW_ICON,
      onSelect: () => setPreviewOpen(true),
    },
    {
      id: "add",
      label: t(ACTION_I18N_KEYS.add),
      icon: PlusCircle,
      hidden: !isAddMode,
      onSelect: onAdd,
    },
  ];

  if (printAction) {
    actions.push({
      id: "print",
      label: t(ACTION_I18N_KEYS.print),
      icon: Download,
      hidden: isAddMode,
      render: () => printAction,
    });
  }

  actions.push({
    id: "delete",
    label: t(ACTION_I18N_KEYS.delete),
    icon: Trash2,
    destructive: true,
    hidden: isAddMode,
    onSelect: onDelete,
  });

  return (
    <div className="flex justify-end" onClick={(event) => event.stopPropagation()}>
      <ShowElements
        id={resourceId}
        type={previewType}
        elements={previewElements}
        onAdd={isAddMode ? onAdd : undefined}
        open={previewOpen}
        onOpenChange={setPreviewOpen}
      />
      <TableRowActionsMenu actions={actions} />
    </div>
  );
}
